import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-full text-2 font-medium focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'default-button bg-cyan-11 text-white hover:bg-cyan-12 focus:bg-cyan-12',
        avatar:
          'ring-offset-background transition-colors focus-visible:ring-2 focus-within:ring-2 ring-ring',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/80',
        destructiveOutline:
          'border border-error-4/40 text-error-4 hover:bg-error-4/20',
        outline:
          'border border-cyan-11 text-cyan-11 focus:bg-cyan-4 hover:bg-cyan-4',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost:
          'ring-offset-background hover:ring-2 hover-ring focus-visible:ring-2 focus-within:ring-2 ring-ring',
        link: 'text-primary',
        disabled: 'bg-mauve-4 text-mauve-9',
        lessonOutline:
          'lesson-outline-button border border-purple-6 text-purple-6 focus:bg-purple-3 hover:bg-purple-3',
        lessonDefault:
          'lesson-default-button mt-4 w-fit bg-purple-6 hover:bg-purple-3 hover:text-purple-6 focus:bg-purple-3 focus:text-purple-6 focus:outline-hidden',
      },
      size: {
        default: 'h-7 px-4',
        wide: 'px-24 py-5',
        xs: 'h-6',
        sm: 'small-button h-6 px-3 py-2',
        lg: 'h-11 rounded-md px-8',
        pill: 'px-12 py-3 leading-3',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
